// Monthly return netting external cash flows (deposits + transfers/withdrawals),
// using the Modified Dietz method (day-weighted by each flow's value-date).
//
// Flow sign convention: amount > 0 = money IN (deposit), amount < 0 = money OUT
// (withdrawal / transfer out). netFlow = Σ amount.

const MS_PER_DAY = 86400000;

export function daysBetween(aIso, bIso) {
  const a = new Date(aIso + "T00:00:00");
  const b = new Date(bIso + "T00:00:00");
  return Math.round((b - a) / MS_PER_DAY);
}

// Modified Dietz period return.
//   r = (V1 - V0 - C) / (V0 + Σ w_i·C_i),   w_i = (D - d_i) / D
// Returns null when the denominator is non-positive (can't define a return).
export function computePeriodReturn({ V0, V1, flows = [], periodStart, periodEnd }) {
  if (V0 == null || V1 == null || !periodStart || !periodEnd) return null;
  const D = daysBetween(periodStart, periodEnd);
  if (D <= 0) return null;

  let netFlow = 0;
  let weighted = 0;
  for (const f of flows) {
    const amt = Number(f.amount) || 0;
    netFlow += amt;
    // clamp the flow's day offset into [0, D]
    let d = f.date ? daysBetween(periodStart, f.date) : D / 2;
    if (d < 0) d = 0;
    if (d > D) d = D;
    weighted += amt * ((D - d) / D);
  }

  const denom = V0 + weighted;
  if (denom <= 0) return null;
  return (V1 - V0 - netFlow) / denom;
}

// Build the signed flow list for a period from parsed fund flows, filtered to
// (periodStart, periodEnd]. deposits -> +amount, withdrawals -> -amount.
export function flowsInInterval(fundFlows, periodStart, periodEnd) {
  const out = [];
  const inRange = (iso) => iso && iso > periodStart && iso <= periodEnd;
  for (const d of fundFlows?.deposits || []) {
    if (inRange(d.valueDate)) out.push({ date: d.valueDate, amount: Math.abs(d.amount) });
  }
  for (const w of fundFlows?.withdrawals || []) {
    if (inRange(w.valueDate)) out.push({ date: w.valueDate, amount: -Math.abs(w.amount) });
  }
  return out;
}

// Sum of deposit/withdrawal amounts in a period (for display + storage).
export function sumFlows(fundFlows, periodStart, periodEnd) {
  const inRange = (iso) => iso && iso > periodStart && iso <= periodEnd;
  let deposits = 0;
  let withdrawals = 0;
  for (const d of fundFlows?.deposits || []) {
    if (inRange(d.valueDate)) deposits += Math.abs(d.amount);
  }
  for (const w of fundFlows?.withdrawals || []) {
    if (inRange(w.valueDate)) withdrawals += Math.abs(w.amount);
  }
  return { deposits, withdrawals };
}

// Break period deposits into buckets for display (does NOT affect the return math —
// all flows are still netted together in computePeriodReturn).
//   oneTime  — incoming transfers / one-off movements (deposit.isTransfer)
//   employer — current/former employer (SUG-MAFKID 2,3)
//   insured  — the insured person (SUG-MAFKID 1,4,5); labeled "עצמאי" when the
//              fund has no employer, otherwise "עובד"
export function sumFlowsByType(fundFlows, periodStart, periodEnd, { hasEmployer = true } = {}) {
  const inRange = (iso) => iso && iso > periodStart && iso <= periodEnd;
  let employer = 0;
  let insured = 0;
  let oneTime = 0;
  let withdrawals = 0;
  for (const d of fundFlows?.deposits || []) {
    if (!inRange(d.valueDate)) continue;
    const amt = Math.abs(d.amount);
    if (d.isTransfer) {
      oneTime += amt;
    } else if (d.sugMafkid === "2" || d.sugMafkid === "3") {
      employer += amt;
    } else {
      insured += amt;
    }
  }
  for (const w of fundFlows?.withdrawals || []) {
    if (inRange(w.valueDate)) withdrawals += Math.abs(w.amount);
  }
  return { employer, insured, oneTime, withdrawals, insuredLabel: hasEmployer ? "עובד" : "עצמאי" };
}

// Chain monthly returns into a cumulative return. Nulls are skipped.
export function cumulativeReturn(returns) {
  let factor = 1;
  let any = false;
  for (const r of returns) {
    if (r == null || Number.isNaN(r)) continue;
    factor *= 1 + r;
    any = true;
  }
  return any ? factor - 1 : null;
}

// Annualize a cumulative return observed over `months` months.
export function annualize(cumR, months) {
  if (cumR == null || !months || months <= 0) return null;
  return Math.pow(1 + cumR, 12 / months) - 1;
}
